import {fromPairs, uniq, zip} from 'ramda';
import {round} from '../Util';
import {ItemCategoryDb} from './Categories';
import Constants from './Constants';
import {ItemDescription} from './ItemDescriptions';
import {isValidQuoteItem} from './Items';
import {Pickup} from './Pickups';

export type SustainabilityImpact = {
  CO2Reduction_lbs: number;
  divertedLandfill_lbs: number;
  treeAbsorptionEquiv: number;
  timeSaved_hours: number;
  water_saved_gallons: number;
  toxic_waste_avoided_lbs: number;
  energy_saved_kwh: number;
};
export const EMPTY_SUSTAINABILITY_IMPACT: SustainabilityImpact = {
  CO2Reduction_lbs: 0,
  divertedLandfill_lbs: 0,
  treeAbsorptionEquiv: 0,
  water_saved_gallons: 0,
  toxic_waste_avoided_lbs: 0,
  energy_saved_kwh: 0,
  timeSaved_hours: 0,
};

type ItemSustainabilityFields = Pick<
  ItemDescription,
  | 'resale_type'
  | 'total_quantity'
  | 'item_category_id'
  | 'set_volume'
  | 'width'
  | 'height'
  | 'depth'
  | 'total_pieces'
>;

export function calculateSustainabilityImpactForMetric({
  metric,
  item,
  category,
}: {
  metric: keyof Omit<
    ItemCategoryDb['sustainability_metrics_factors'],
    'recycling_factor' | 'multiplier_feature'
  >;
  item: ItemSustainabilityFields;
  category: Maybe<ItemCategoryDb>;
}): number {
  if (!category?.sustainability_metrics_factors) return 0;
  if (!category.sustainability_metrics_factors[metric]) return 0;
  return (
    (item.total_quantity || 0) *
    (category.sustainability_metrics_factors[metric] || 0) *
    (item.resale_type === Constants.ResaleType.RECYCLE
      ? category.sustainability_metrics_factors['recycling_factor'] || 0
      : 1) *
    (category.sustainability_metrics_factors['multiplier_feature'] === 'volume' &&
    metric !== 'CO2_emissions_avoided_lbsCO2equivalent'
      ? item.set_volume ||
        ((item.depth || 0) / 12) * ((item.height || 0) / 12) * ((item.width || 0) / 12) // item dimensions are in inches
      : category.sustainability_metrics_factors['multiplier_feature'] === 'total_pieces'
      ? item.total_pieces || 0
      : 1)
  );
}
export function calculateTimeSaved_hours(items: ItemSustainabilityFields[]) {
  const auctionItems = items.filter(item => item.resale_type === Constants.ResaleType.AUCTION);
  const consignmentItems = items.filter(
    item => item.resale_type === Constants.ResaleType.CONSIGNMENT,
  );
  const donationItems = items.filter(item => item.resale_type === Constants.ResaleType.DONATION);
  const recycleItems = items.filter(item => item.resale_type === Constants.ResaleType.RECYCLE);
  const rates = {
    auction: auctionItems.length <= 5 ? 4 : auctionItems.length <= 10 ? 3.5 : 3,
    consignment: consignmentItems.length <= 5 ? 4 : consignmentItems.length <= 10 ? 3.5 : 3,
    donation: donationItems.length <= 5 ? 0.5 : donationItems.length <= 10 ? 0.35 : 0.25,
    recycle: 2,
  };
  return (
    auctionItems.length * rates.auction +
    consignmentItems.length * rates.consignment +
    donationItems.length * rates.donation +
    (recycleItems.length > 0 ? rates.recycle : 0)
  );
}
export function calculateSustainabilityImpact(
  items: ItemSustainabilityFields[],
  categoriesDb: ItemCategoryDb[],
): SustainabilityImpact {
  const categoriesMap = categoriesMapper(categoriesDb);
  const sum = (a: number, b: number) => a + b;
  const allItemsSustainabilityMetricsValues = (
    [
      'CO2_emissions_avoided_lbsCO2equivalent',
      'landfill_diverted_lbs',
      'tree_absorption_equivalent',
      'water_saved_gallons',
      'toxic_waste_avoided_lbs',
      'energy_saved_kWh',
    ] as const
  ).map(metric =>
    items
      .map(item =>
        calculateSustainabilityImpactForMetric({
          metric,
          item,
          category: categoriesMap[item.item_category_id || 0],
        }),
      )
      .reduce((a, b) => round(sum(a, b), 0), 0),
  );
  const sustainbilityMetricsTypeKeys = [
    'CO2Reduction_lbs',
    'divertedLandfill_lbs',
    'treeAbsorptionEquiv',
    'water_saved_gallons',
    'toxic_waste_avoided_lbs',
    'energy_saved_kwh',
    'timeSaved_hours',
  ] as const;
  return fromPairs(
    zip(
      sustainbilityMetricsTypeKeys,
      allItemsSustainabilityMetricsValues.concat(calculateTimeSaved_hours(items)),
    ),
  ) as SustainabilityImpact;
}
export function getQuoteItems(pickup: Pickup) {
  return pickup.consultation_items.filter(isValidQuoteItem).filter(item => !!item.item_category_id);
}
export function quoteCategoryIds(pickup: Pickup): number[] {
  return uniq(
    getQuoteItems(pickup)
      .map(item => item.item_category_id)
      .filter(Boolean),
  );
}
export function quoteSustainabilityImpact(
  pickup: Pickup,
  categoriesDb: ItemCategoryDb[],
): SustainabilityImpact {
  return calculateSustainabilityImpact(getQuoteItems(pickup), categoriesDb);
}
export function pickupsSustainabilityImpact(
  items: ItemSustainabilityFields[],
  categoriesDb: ItemCategoryDb[],
): SustainabilityImpact {
  return calculateSustainabilityImpact(items, categoriesDb);
}
export function categoriesMapper(categories: ItemCategoryDb[]): {
  [categoryId: number]: ItemCategoryDb;
} {
  const categoriesMap: {[categoryId: number]: ItemCategoryDb} = {};
  uniq(categories).forEach(category =>
    !!category.id || category.id === 0 ? (categoriesMap[category.id] = category) : null,
  );
  return categoriesMap;
}
